import type { ReactNode } from "react";

/**
 * The layout every page inside the shell renders into: a title with its actions, then a 12-column
 * body.
 *
 * A compound component rather than three loose exports so the pairing is enforced at the call site
 * — `Page.Body` is a grid whose columns only line up because `Page` established them, and a body
 * used on its own would silently lay out against whatever grid it happened to land in.
 *
 * The twelve columns are what the financial modules will need: a summary card spanning the full
 * width, three stat cards at four columns each, a chart at eight beside a legend at four. Nothing
 * in the app claims more than `col-span-full` yet, and that is fine — the grid costs nothing until
 * something uses it.
 */
function PageRoot({ children }: { children: ReactNode }): ReactNode {
  return <div className="grid grid-cols-12 gap-y-10">{children}</div>;
}

/**
 * The title row, and with it THE ONE PLACE A PAGE'S PRIMARY ACTIONS GO: the end of the title's line,
 * right-aligned.
 *
 * A PROP RATHER THAN A `Page.Actions` SIBLING, and that is the whole point. A sibling component
 * could be rendered anywhere in the tree and would still compile, so "the standard location" would
 * be a convention someone has to remember; as a prop on the title there is nowhere else to put it.
 * Every page's primary control then lands in the same spot without any page deciding so.
 *
 * `flex-wrap` with `justify-between`, so a long title and a wide action do not collide on a narrow
 * viewport — the actions drop to their own line and left-align under the heading instead.
 *
 * `items-baseline` is deliberately NOT used: the actions are controls, not text, and aligning a
 * 36px button to a 40px heading's baseline hangs it below the cap height. Centres read level.
 */
function PageTitle({ children, actions }: { children: ReactNode; actions?: ReactNode }): ReactNode {
  return (
    <div className="col-span-full flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
      <h1 className="text-3xl font-semibold text-ink md:text-4xl">{children}</h1>
      {/* Rendered only when there are actions, so a page without any has no empty flex child
          nudging the heading's alignment. A row rather than a single slot: a page may pair a
          secondary control with its primary one, and they belong side by side here. */}
      {actions ? <div className="flex shrink-0 items-center gap-3">{actions}</div> : null}
    </div>
  );
}

/**
 * `data-testid` IS PART OF THE CONTRACT HERE, and it is not decoration.
 *
 * A layout assertion needs a denominator. Measuring one section against a NEIGHBOUR — "the ranking is
 * wider than the chart above it" — encodes a second layout decision into the check, so the check goes
 * stale the moment the neighbour moves, and it goes stale SILENTLY: a ratio that no longer holds passes
 * exactly as quietly as one that does. The dashboard's did that twice in one day.
 *
 * The body's own box is the one width on a page that no section can change, so it is what a "half the
 * row" or "full width" claim should be measured against.
 */
function PageBody({ children }: { children: ReactNode }): ReactNode {
  return (
    <div className="col-span-full grid grid-cols-12 gap-x-5 gap-y-10" data-testid="page-body">
      {children}
    </div>
  );
}

export const Page = Object.assign(PageRoot, {
  Title: PageTitle,
  Body: PageBody,
});
